import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import {
    AssetT as ApiAssetT,
    AssetAttribute,
    AssetType,
    preloadAssets,
    refreshAssets,
} from "tweenly"
import { Asset } from "tweenly/dist/fetchAsset"
import { Status } from "../../constants/status"
import { get, post, postFile, put } from "../../helpers/ajax.helpers"
import { S3_PREFIX } from "../../helpers/file.helpers"
import { getAssetTypeMsg } from "../../helpers/graphic.helpers"
import { useToast } from "../../hooks/useToast"
import { AppThunkT } from "../store"
import { CompanyT } from "./company.slice"
import { getCompanyStatsAsync } from "./companyStats.slice"
import { AssetRef } from "./graphics.slice"
import { updateMasterTimeline } from "./masterTimeline.slice"
import { UserT } from "./user.slice"

export type CreateAssetT = {
    name: string
    slug: string
    url?: string
    type: AssetType
    attributes: AssetAttribute[]
    companyId: string
    size?: number
    description: string
    tags?: string[]
    urls?: string[]
    refresh?: boolean
    refreshInterval?: number
}

export type AssetT = Omit<ApiAssetT, "attributes" | "description"> & {
    attributes: AssetAttribute[]
    description: string
    user?: UserT | null
    company?: CompanyT
    isPublished?: boolean
    updatedById?: string
    updatedBy?: UserT | null
    price: number
    revision?: number
    isFinal?: boolean
    isExported?: boolean
    thumbnailId?: string
    fileId?: string
    assetsRef?: AssetRef[]
}

type AssetsStateT = {
    data: AssetT[]
    status?: string
    pendingRefreshAssets?: boolean
    pendingPreloadAssets?: boolean
}
const initialState: AssetsStateT = {
    data: [],
    pendingRefreshAssets: false,
    pendingPreloadAssets: false,
}

const DATA_SOURCE_ASSET_TYPES: AssetType[] = ["json", "xml", "text"]

const isDataSourceAsset = (asset: Pick<AssetT, "type">) =>
    asset.type !== undefined && DATA_SOURCE_ASSET_TYPES.includes(asset.type)

const getAssetTime = (asset: AssetT) => {
    const time = new Date(asset.updated).getTime()
    return Number.isNaN(time) ? 0 : time
}

const getNewestAsset = (assets: AssetT[]) =>
    [...assets].sort((a, b) => getAssetTime(b) - getAssetTime(a))[0]

// _id: undefined ensures it's omitted from JSON serialization, preventing MongoDB duplicate key errors
const toAssetPayload = (data: CreateAssetT, companyId: string): CreateAssetT =>
    ({ ...data, companyId, _id: undefined } as CreateAssetT)

export const assetsSlice = createSlice({
    name: "assets",
    initialState,
    reducers: {
        getAssetsPending: (state: AssetsStateT) => {
            state.data = initialState.data
            state.status = undefined
        },
        getAssetsDeletePending: (state: AssetsStateT) => {
            state.status = undefined
        },
        createAssetsFulfilled: (state: AssetsStateT, action: PayloadAction<AssetT[]>) => {
            state.status = Status.OK
            state.data = [...state.data, ...action.payload]
        },
        updateAssetFulfilled: (state: AssetsStateT, action: PayloadAction<AssetT>) => {
            state.status = Status.OK
            state.data = state.data.map((asset: AssetT) =>
                asset._id === action.payload._id ? { ...asset, ...action.payload } : asset
            )
        },
        deleteAssetFulfilled: (state: AssetsStateT, action: PayloadAction<{ _id: string }>) => {
            state.data = state.data.filter((asset) => asset._id !== action.payload._id)
        },
        getAssetsFulfilled: (state: AssetsStateT, action: PayloadAction<AssetT[]>) => {
            state.status = Status.OK
            state.data = action.payload
        },
        getAssetsRejected: (state: AssetsStateT, action: PayloadAction<string>) => {
            state.status = action.payload
        },
        setPendingRefreshAssets: (state: AssetsStateT, action: PayloadAction<boolean>) => {
            state.pendingRefreshAssets = action.payload
        },
        setPendingPreloadAssets: (state: AssetsStateT, action: PayloadAction<boolean>) => {
            state.pendingPreloadAssets = action.payload
        },
    },
})

export const {
    getAssetsPending,
    getAssetsDeletePending,
    createAssetsFulfilled,
    updateAssetFulfilled,
    deleteAssetFulfilled,
    getAssetsFulfilled,
    getAssetsRejected,
    setPendingRefreshAssets,
    setPendingPreloadAssets,
} = assetsSlice.actions

export const createFontsAsync =
    (files: File[], companyId: string, cb?: (asset: AssetT) => void): AppThunkT =>
    async (dispatch, getState) => {
        const { graphic } = getState()
        const graphicId = graphic.data._id

        try {
            const assets = await Promise.all(
                files.map(async (file) => {
                    const fileMetadata = await postFile(file, {
                        storage: "s3",
                        isPublic: true,
                        hasFilename: true,
                        key: `${S3_PREFIX}/${graphicId}/fonts`,
                    })

                    const data: CreateAssetT = {
                        name: fileMetadata.filename.substring(
                            0,
                            fileMetadata.filename.lastIndexOf(".")
                        ),
                        slug: fileMetadata._id,
                        companyId,
                        attributes: [],
                        url: fileMetadata.publicUrl,
                        type: "font",
                        size: fileMetadata.length,
                        description: "",
                    }
                    const { _id } = await post<{ _id: string }>(`assets`, data)
                    const asset: AssetT = {
                        ...data,
                        _id,
                        companyId,
                        created: new Date(),
                        updated: new Date(),
                        price: 0,
                    }
                    return asset
                })
            )

            cb?.(assets[0])
            dispatch(createAssetsFulfilled(assets))
            if (companyId) dispatch(getCompanyStatsAsync(companyId, true))
        } catch (error: any) {
            dispatch(getAssetsRejected(error.message))
        }
    }

const createAsset = async (data: CreateAssetT, companyId: string) => {
    const createPayload = toAssetPayload(data, companyId)
    let asset: AssetT
    const { _id } = await post<{ _id: string }>(`assets`, {
        ...createPayload,
        companyId,
    })
    asset = {
        ...createPayload,
        _id,
        companyId,
        created: new Date(),
        updated: new Date(),
        price: 0,
    }
    return asset
}

/** Returns the created asset, or null when the creation failed (the error is reported to the store) */
export const createAssetAsync =
    (data: CreateAssetT, companyId: string): AppThunkT<Promise<AssetT | null>> =>
    async (dispatch) => {
        try {
            const asset = await createAsset(data, companyId)
            dispatch(createAssetsFulfilled([asset]))
            if (companyId) dispatch(getCompanyStatsAsync(companyId, true))
            const { success } = useToast()
            const assetTypeMsg = asset.type ? getAssetTypeMsg(asset.type) : ""
            success("dataSource:successMessage.assetsCreate" + assetTypeMsg)
            return asset
        } catch (error: any) {
            dispatch(getAssetsRejected(error.message))
            return null
        }
    }

export const updateAssetAsync =
    (id: string, asset: AssetT): AppThunkT =>
    async (dispatch) => {
        const companyId = asset.companyId || asset.company?._id || ""
        const updatePayload = toAssetPayload(asset, companyId)

        try {
            await put<AssetT>(`assets/${id}`, updatePayload)
            const updatedAsset: AssetT = { ...asset, companyId, _id: id }
            dispatch(updateAssetFulfilled(updatedAsset))
            if (companyId) dispatch(getCompanyStatsAsync(companyId, true))
            const { success } = useToast()
            success("dataSource:successMessage.assetsUpdate")
        } catch (error: any) {
            dispatch(getAssetsRejected(error.message))
        }
    }

/**
 * soft delete asset, only from state in slice
 */
export const softDeleteAssetAsync =
    (id: string): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(getAssetsDeletePending())
            dispatch(deleteAssetFulfilled({ _id: id }))
        } catch (error: any) {
            dispatch(getAssetsRejected(error.message))
        }
    }

const resolveImportedAsset = async (
    localAsset: AssetT,
    companyId: string,
    existingAssets: AssetT[]
) => {
    // companyId can be an empty string when the graphic context is not yet loaded
    const effectiveCompanyId = companyId || localAsset.companyId

    if (isDataSourceAsset(localAsset)) {
        const existingAsset = existingAssets.find((asset) => asset._id === localAsset._id)
        if (existingAsset) {
            return existingAsset
        }

        if (!effectiveCompanyId) {
            return localAsset
        }

        const currentCompanyAssets = await get<AssetT[]>(`assets/similar`, {
            slug: localAsset.slug,
            companyId: effectiveCompanyId,
        })
        const currentCompanyAsset = getNewestAsset(
            currentCompanyAssets.filter((asset) => asset.slug === localAsset.slug)
        )

        return currentCompanyAsset ?? (await createAsset(localAsset, effectiveCompanyId))
    }

    const existingAsset = existingAssets.find((asset) => asset._id === localAsset._id)
    return existingAsset ?? (await createAsset(localAsset, effectiveCompanyId))
}

export const getGraphicAssetsAsync =
    (localAssets: AssetT[], companyId: string): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(getAssetsPending())
            if (localAssets.length) {
                dispatch(setPendingPreloadAssets(true))
                try {
                    const existingAssets: AssetT[] = await get<AssetT[]>(`assets`, {
                        id: localAssets.map((asset) => asset._id),
                    })

                    const resolvedAssets = await Promise.all(
                        localAssets.map((localAsset) =>
                            resolveImportedAsset(localAsset, companyId, existingAssets)
                        )
                    )

                    await preloadAssets(resolvedAssets as Asset[])
                    dispatch(getAssetsFulfilled(resolvedAssets))
                    if (companyId) dispatch(getCompanyStatsAsync(companyId, true))
                } finally {
                    dispatch(setPendingPreloadAssets(false))
                }
            } else {
                dispatch(getAssetsFulfilled([]))
            }
        } catch (error: any) {
            dispatch(getAssetsRejected(error.message))
        }
    }
export const refreshAssetsAsync = (): AppThunkT => async (dispatch, getState) => {
    try {
        const assets = getState().assets.data
        const timeToSeek = getState().masterTimeline.time
        dispatch(setPendingRefreshAssets(true))
        await refreshAssets(assets as Asset[])
        dispatch(setPendingRefreshAssets(false))

        const masterTimeline = getState().masterTimeline.value
        masterTimeline?.clear()
        dispatch(updateMasterTimeline(timeToSeek))
    } catch (error: any) {
        dispatch(getAssetsRejected(error.message))
    }
}
export default assetsSlice.reducer
